<template>
  <v-card class="grayBorder" flat>
    <v-card-title class="d-flex flex-row justify-space-between cardTitle">
      <div v-if="isYear">1 Year Supply</div>
      <div v-if="!isYear">Other Supply</div>

      <v-fade-transition>
        <div v-if="!isYear" class="d-print-none">
          <v-btn
            v-for="(supplyObj, months) in supplyOptionsOU"
            :key="months"
            x-small
            rounded
            depressed
            class="mx-1"
            :color="bilateralPartialSupply == months ? 'primary' : null"
            @click="setSupply(supplyObj)"
          >
            {{ months }} Mo.
          </v-btn>
        </div>
      </v-fade-transition>
    </v-card-title>
    <v-card-subtitle class="caption cardTitle my-1">
      <div v-if="!isYear && partialSupplyDescription" class="text-body-1">
        {{ partialSupplyDescription }}
      </div>
      <div v-else class="emptyDivHeight">&nbsp;</div>
    </v-card-subtitle>

    <v-card-text class="d-flex flex-column text-body-1 darkerCardText">
      <div
        v-for="(eye, index) in ['od', 'os']"
        :key="index"
        class="pa-2 align-center"
        :class="eye == 'od' ? 'greenBkg' : 'blueBkg'"
      >
        <!-- <div class="body-1 my-1">Right Eye</div> -->
        <div class="d-flex flex-row align-center justify-space-between">
          <div class="text-h6 mr-2" v-show="$vuetify.breakpoint.mdAndUp">
            {{ eye | eyeLetter }}
          </div>

          <!-- for printing -->
          <div
            v-if="!isYear && selectedSupply[eye]"
            class="d-none d-print-block"
          >
            <span v-show="qty[eye] > 0" class="body-1">
              <span class="mr-2">{{ qty[eye] }} x </span>
              <span class="mr-2">
                {{ selectedSupply[eye].retail | currency }} /
                {{ selectedSupply[eye].quantity }}pk
              </span>
            </span>
          </div>

          <div v-if="isYear">
            <span v-show="yearSupplyInfo[eye].boxes > 0" class="body-1">
              <span class="mr-2">{{ yearSupplyInfo[eye].boxes }} x </span>
              <span class="mr-2">
                {{ yearSupplyInfo[eye].unitPrice | currency }} /
                {{ yearSupplyInfo[eye].packSize }}pk
              </span>
            </span>
          </div>

          <v-text-field
            v-if="!isYear"
            v-model="qty[eye]"
            label="Boxes"
            class="smallNum d-print-none"
            type="number"
            min="0"
            max="12"
            dense
            hide-details="true"
          ></v-text-field>

          <v-select
            v-if="!isYear"
            v-model="selectedSupply[eye]"
            :items="boxOptions[eye]"
            label="Pkg Options"
            class="supplyInput d-print-none"
            hide-details="true"
            dense
          >
            <template v-slot:item="{ item }">
              {{ item.retail | currency }} / {{ item.quantity + "pk" }}
            </template>
            <template v-slot:selection="{ item }">
              {{ item.retail | currency }} / {{ item.quantity + "pk" }}
            </template>
          </v-select>

          <div class="medNum">
            {{ eyeTotal[eye] | currency }}
          </div>
        </div>
      </div>

      <ledger-row
        v-if="feesDiscounts.willShip"
        title="Shipping"
        :value="shipping != 0 ? shipping : 'Free'"
        :valueClass="shipping == 0 ? 'font-italic' : ''"
      ></ledger-row>
      <v-divider></v-divider>
      <ledger-row
        title="Subtotal"
        :value="subtotal + shipping"
        titleClass="font-weight-bold"
        valueClass="font-weight-bold"
      ></ledger-row>

      <ledger-row
        v-if="feesDiscounts.insurance && feesDiscounts.insurance > 0"
        title="Ins. Contribution"
        :value="feesDiscounts.insurance"
        sign="-"
      ></ledger-row>

      <ledger-row
        v-if="feesDiscounts.yearDiscount && feesDiscounts.yearDiscount > 0"
        title="Year supply discount"
        :value="isYear ? feesDiscounts.yearDiscount : 0"
        sign="-"
      ></ledger-row>

      <v-divider></v-divider>

      <ledger-row
        title="In Office Total"
        :value="total"
        titleClass="text-h5"
        valueClass="text-h5"
      ></ledger-row>

      <v-divider></v-divider>
      <div class="emptyDivHeight">
        <add-rebate-dialog
          :currentRebate="rebate"
          @rebate="rebate = $event"
          v-if="isYear && rebate < 1"
        ></add-rebate-dialog>
        <ledger-row
          title="Rebate"
          :value="-rebate"
          v-if="isYear && rebate > 0"
        ></ledger-row>
      </div>
      <div
        class="d-flex flex-row align-start justify-space-between my-2 pa-2"
        v-if="costToPatient >= 0 && (isYear || feesDiscounts.showYearCost)"
      >
        <div class="mt-2">
          <div class="text-h6">
            {{ isYear ? "Yearly Cost to Patient" : "Yearly Cost (Projected) " }}
          </div>

          <div v-if="!isYear && remainingMonthsSupply" class="mx-1 text-body-2">
            Completing this year supply will cost an additional
            {{ remainingMonthsSupply.total | currency }}.
            <ul v-for="eye in ['od', 'os']" :key="eye">
              <li
                v-for="(desc, index) in remainingMonthsSupply.description[eye]"
                :key="index"
              >
                {{ desc }}
              </li>
            </ul>
            <!-- <span class="mx-1">{{ total | currency }}</span> +
            
            =
            <span class="mx-1">{{ costToPatient | currency }}</span> -->
          </div>
        </div>
        <div>
          <v-card
            class="pa-3 text-center shapeBorder"
            color="#fcf0ff"
            flat
            shaped
            outlined
          >
            <div class="text-h6">
              {{ costToPatient | currency }}
            </div>
            <div class="caption">
              {{ (costToPatient / 12) | currency }} / month
            </div>
          </v-card>
        </div>
      </div>
      <v-card
        v-if="savings && feesDiscounts.showSavings"
        flat
        rounded
        outlined
        color="yellow lighten-5"
        class="text-center pt-1"
      >
        Year supply savings: {{ savings | currency }}
      </v-card>
    </v-card-text>
  </v-card>
  <!-- {{ boxOptions }} -->
</template>

<script>
import AddRebateDialog from "./AddRebateDialog";
import LedgerRow from "./LedgerRow";
// import Name from "../dir/file";

export default {
  name: "SupplyCalculator",
  components: {
    AddRebateDialog,
    LedgerRow,
  },
  props: {
    feesDiscounts: {
      insurance: 0,
      yearDiscount: 50,
      shippingPartial: 9.99,
      shippingYear: 0,
      willShip: true,
    },

    isYear: {
      type: Boolean,
      default: false,
    },
    priceData: {
      // will need to extract family data: priceData[eye][practice][boxes][boxsize=>{}]
      type: Object,
      default: function () {
        return null;
      },
    },
    savings: {
      type: Number,
      default: null,
    },
  },
  data: function () {
    return {
      rebate: 0,
      otherAdj: 0,
      qty: { od: null, os: null },
      amt: { od: null, os: null },
      selectedSupply: { od: null, os: null },

      // hasRebate: false,
    };
  },
  methods: {
    setSupply(supplyObj) {
      // console.log(supplyObj);
      for (let eye of ["od", "os"]) {
        let boxes = supplyObj[eye].boxes ? supplyObj[eye].boxes : 0;
        this.qty[eye] = boxes;
        let supplyPackSize = supplyObj[eye].packSize;
        let selSupply = null;
        if (supplyPackSize) {
          let spsString = `${supplyPackSize}`;
          let boxObj = this.priceData[eye]?.practice?.boxes?.[spsString];
          selSupply = boxObj;
        }
        this.selectedSupply[eye] = selSupply;
      }
    },
    supplyForEye(eye) {
      return this.priceData[eye]?.practice?.supplies;
    },
    // if months specified, returns the supply with the key=months
    // if not specified, returns all the months
    getSupply(eye, months) {
      const supply = this.priceData[eye]?.practice?.supplies;
      if (!supply) return null;
      if (!months) return supply;
      return supply[months.toString()];
    },
    closestSupplyForMonths(eye, months) {
      const supplies = this.priceData[eye]?.practice?.supplies;
      if (!supplies) return null;
      let monthsLeft = 9999;
      let bestSupply = {};
      for (let [duration, supply] of Object.entries(supplies)) {
        if (duration > months) continue;
        // console.log(duration, months);
        //for (let supply of supplies) {
        //let duration = supply.months;
        let tempRemaining = Math.max(months - duration, 0);
        if (tempRemaining < monthsLeft) {
          monthsLeft = tempRemaining;
          bestSupply = supply;
        }
      }
      return bestSupply;
    },
    // returns an array of supplies needed to satisfy a duration
    // for example, if need 9 months will return a 6 and 3 months supply
    supplyForMonths(eye, months) {
      let supplies = [];
      let description = [];
      let totalPrice = 0;
      let monthsLeft = months;
      let eyeLetter = eye == "os" ? "Left" : "Right";
      while (monthsLeft > 0) {
        let supply = this.closestSupplyForMonths(eye, monthsLeft);
        if (!supply) break;
        supplies.push(supply);
        totalPrice += parseFloat(supply.totalPrice);

        let desc = `${eyeLetter}: ${supply.boxes} box x $${supply.unitPrice} / ${supply.packSize}pk`;
        description.push(desc);
        monthsLeft = monthsLeft - supply.months;
      }
      return {
        supplies: supplies,
        totalPrice: totalPrice,
        description: description,
      };
    },
    // monthsForSupply(qty, supply) {
    //   let months = 0;
    //   if (supply) {
    //     let duration = qty * supply.quantity * supply.replace_simple;
    //     if (duration) months = Math.round(duration / 30);
    //   }
    //   return months;
    // },
  },
  computed: {
    eyeTotal: function () {
      let eyeTotal = { od: 0, os: 0 };
      for (const eye of ["od", "os"]) {
        if (this.isYear) {
          let yearSupplyTotal = this.yearSupplyInfo[eye].totalPrice;
          if (yearSupplyTotal > 0) eyeTotal[eye] = parseFloat(yearSupplyTotal);
        } else {
          let retail = this.selectedSupply[eye]?.retail;
          if (retail) eyeTotal[eye] = retail * this.qty[eye];
        }
      }
      return eyeTotal;
    },
    bothEyeTotal: function () {
      let odTotal = parseFloat(this.eyeTotal.od);
      let osTotal = parseFloat(this.eyeTotal.os);
      return odTotal + osTotal;
    },
    shipping: function () {
      if (!this.feesDiscounts.willShip) return 0;
      let shipping = this.isYear
        ? this.feesDiscounts.shippingYear
        : this.feesDiscounts.shippingPartial;
      return parseFloat(shipping);
    },
    subtotal: function () {
      let bothEyes = this.bothEyeTotal;
      return bothEyes ? bothEyes + parseFloat(this.shipping) : 0;
    },
    total: function () {
      let subtotal = this.subtotal;
      if (!subtotal) return 0;
      let less = parseFloat(this.feesDiscounts.insurance);
      if (this.isYear) less += parseFloat(this.feesDiscounts.yearDiscount);
      return Math.max(subtotal - less, 0);
    },
    costToPatient: function () {
      let total = this.total;
      if (this.isYear) {
        total -= this.rebate;
      } else {
        let remainingCost = this.remainingMonthsSupplyCost;
        // we don't want to show cost to patient if we can't calculate it
        if (!remainingCost) return 0;
        total += parseFloat(remainingCost);
      }
      if (!total || total < 0) total = 0;
      return total;
    },
    // classForSize: function () {
    //   let daClass = "xsClass";
    //   if (this.$vuetify.breakpoint.smAndUp) daClass = "smClass";
    //   if (this.$vuetify.breakpoint.mdAndUp) daClass = "mdClass";
    //   return daClass;
    // },
    monthsForSupply: function () {
      let months = { od: 0, os: 0 };
      let supOD = this.selectedSupply.od;
      let supOS = this.selectedSupply.os;
      let qtyOD = this.qty.od;
      let qtyOS = this.qty.os;

      /*
       * note: multiplying the supply days x 1.05 addresses the problem where larger supplies get rounded a little too low. For example 2 12packs of a 2 week lens = 2x12x14 = 336 days, which is closer to 11 months than it is to 12.
       */

      if (supOD && qtyOD) {
        let durationOD = qtyOD * supOD.daysPerPackage * 1.05;
        // console.log("duration od", durationOD);
        if (durationOD) months.od = Math.round(durationOD / 30);
      }
      if (supOS && qtyOS) {
        let durationOS = qtyOS * supOS.daysPerPackage * 1.05;
        // console.log("duration os", durationOS);
        if (durationOS) months.os = Math.round(durationOS / 30);
      }

      return months;
    },
    // convenience function
    remainingMonthsSupplyCost: function () {
      let rms = this.remainingMonthsSupply;
      return rms && rms.total ? rms.total : null;
    },
    // calculate remaining supply, return {total:xx, description{od:". . .", od:"..."}}
    remainingMonthsSupply: function () {
      let rtn = {};
      let months = this.monthsForSupply;

      rtn.description = { od: [], os: [] };
      let count = 0; // only return data if count=2 (we have info for both eyes)
      rtn.total = 0;
      for (let eye of ["od", "os"]) {
        if (!months[eye]) continue;
        let remainingForEye = Math.max(12 - months[eye], 0);
        let rSupply = this.supplyForMonths(eye, remainingForEye);
        if (rSupply) {
          count++;
          rtn.total += rSupply.totalPrice;
          rtn.description[eye] = rSupply.description; // an array of text descriptions
        }
      }

      return count == 2 ? rtn : null; // only care about year supplies for BOTH eyes
    },
    partialSupplyDescription: function () {
      // let months = {}; //this.monthsForPartialSupply();
      // months.od = this.monthsForSupply(this.qty.od, this.selectedSupply.od);
      // months.os = this.monthsForSupply(this.qty.os, this.selectedSupply.os);
      let months = this.monthsForSupply;
      let areSame = months.od == months.os;
      let rtn = "";
      if (!areSame) {
        if (months.od > 0) rtn += `Right: ${months.od} month supply`;
        if (months.od > 0 && months.os > 0) rtn += ", ";
        if (months.os > 0) rtn += `Left: ${months.os} month supply`;
      } else if (months.od > 0) {
        rtn += `${months.od} month supply `;
      }
      return rtn;
    },

    // returns the supply in months if the od supply=os supply
    // just to highlight the correct supplyOptionsOU button
    bilateralPartialSupply: function () {
      let months = this.monthsForSupply;
      let areSame = months.od == months.os;
      if (!areSame) return null;
      return months.od;
    },

    boxOptions: function () {
      let rtn = { od: [], os: [] };
      for (let eye of ["od", "os"]) {
        let boxesForEye = this.priceData[eye]?.practice?.boxes;
        // console.log(boxesForEye);
        if (!boxesForEye) continue;
        for (const key of Object.keys(boxesForEye)) {
          rtn[eye].push(boxesForEye[key]);
        }
        // for (let [qty, boxObj] of Object.entries(boxesForEye) ) {
        //   rtn.push(boxObj);
        // }
      }
      return rtn;
    },
    // an array of available supply durations, eg {od:[12,6,3], os:[. . .]}
    supplyDurations: function () {
      let supplies = { od: [], os: [] };
      const supplyOD = this.priceData?.od?.practice?.supplies;
      const supplyOS = this.priceData?.os?.practice?.supplies;
      if (supplyOD) supplies.od = Object.keys(supplyOD);
      if (supplyOS) supplies.os = Object.keys(supplyOS);
      return supplies;
    },
    supplyOptionsOU: function () {
      let options = {};

      let durationsOD = this.supplyDurations.od;
      let durationsOS = this.supplyDurations.os;
      for (let option of ["1", "3", "6"]) {
        let inOD = durationsOD.includes(option);
        let inOS = durationsOS.includes(option);

        let optionObjOD = inOD ? this.getSupply("od", option) : null;
        let optionObjOS = inOS ? this.getSupply("os", option) : null;
        let combinedObj = { od: optionObjOD, os: optionObjOS };
        if (inOD && inOS) options[option] = combinedObj;
        if (inOD && durationsOS.length < 1) options[option] = combinedObj;
        if (inOS && durationsOD.length < 1) options[option] = combinedObj;
      }
      // console.log(keysOD);
      return options;
    },
    yearSupplyInfo: function () {
      let rtn = {};
      for (let eye of ["od", "os"]) {
        rtn[eye] = { boxes: 0, price: 0, pack: 0, totalPrice: 0 };
        const supply12 = this.getSupply(eye, 12);
        if (supply12) rtn[eye] = supply12;
      }
      return rtn;
    },
  },

  watch: {
    costToPatient(val) {
      this.$emit("costToPatient", val);
    },
    // if a brand is chosen and a partial supply hasn't been set, automatically use the 6 month supply
    priceData: {
      handler: function (val) {
        // console.log("val");
        // console.log(val);
        let desiredSupply = "6";
        // if (this.selectedSupply.od || this.selectedSupply.os) return;
        for (let eye of ["od", "os"]) {
          const supply = val[eye]?.practice?.supplies;
          const boxes = this.priceData[eye]?.practice?.boxes;

          if (!supply || !boxes) {
            // if no price data, clear the data and the form
            this.qty[eye] = null;
            this.selectedSupply[eye] = null;
            continue;
          }

          // if the other eye has a supply set (eg 3 mo) try to use the same supply duration
          let otherEye = eye == "od" ? "os" : "od";
          let currentMonths = this.monthsForSupply;

          if (currentMonths[eye]) {
            desiredSupply = `${currentMonths[eye]}`;
          } else if (currentMonths[otherEye]) {
            desiredSupply = `${currentMonths[otherEye]}`;
          }

          if (supply && supply[desiredSupply] && boxes) {
            this.qty[eye] = supply[desiredSupply].boxes;
            let packSize = supply[desiredSupply].packSize;
            let supplyObj = boxes[`${packSize}`];
            if (supplyObj) this.selectedSupply[eye] = supplyObj;
          }
        }

        this.$forceUpdate();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.totalDiv {
  width: 85px;
  text-align: right;
}
/* .mdClass {
  min-width: 350px;
  width: calc((95vw - 300px) / 2);
}
.smClass {
  width: 100%;
}
.xsClass {
  width: 100%;
} */
.supplyInput {
  max-width: 150px;
}
.emptyDivHeight {
  height: 24px;
}

.width50 {
  min-width: 50px;
}
.shapeBorder {
  border-color: #ad8db4 !important;
  min-width: 130px;
}

/* .cardTitle {
  background-color: #fff4e6;
  border-bottom: 1px solid gray !important;
} */

@media print {
  .mdClass,
  .smClass {
    width: 45%;
  }
  .greyBorder {
    margin: 0 1em;
  }
}
</style>
